<template>
    <Modal close-esc-event="keydown" :close-button="false" modal-class="w-full max-w-xl bg-white m-auto rounded-xl border-0" mask-class="grid" @state="captureModalApi">
        <template #toggle="scope">
            <Button v-if="showTrigger" variant="default" size="sm" class="self-center" @click="() => openAndLoad(scope.open)">
                <Target class="h-3.5 w-3.5" />
                {{ $t('views.objectives.modal.openButton') }}
            </Button>
        </template>

        <template #header="scope">
            <CloseModalButton @close="() => onClose(scope.close)"></CloseModalButton>
        </template>

        <template #body="scope">
            <div class="w-full">
                <div class="space-y-7 px-8 pb-6 pt-7">
                    <!-- Title + intent -->
                    <div class="space-y-1.5">
                        <h1 class="text-2xl font-semibold text-gray-900">{{ $t('views.objectives.modal.title') }}</h1>
                        <p class="text-sm text-gray-500">{{ $t('views.objectives.modal.description') }}</p>
                    </div>

                    <!-- Animal-type switch (drives the global pig type, so the chart behind reloads too). -->
                    <div class="border-b border-gray-200 pb-4">
                        <!-- Fixed height so this toggle matches the page-header one (which renders ~46px via h-full). -->
                        <div style="height: 46px">
                            <PigTypePicker slim />
                        </div>
                    </div>

                    <!-- Directory-wide default -->
                    <div class="space-y-2">
                        <h2 class="text-base font-semibold text-gray-900">{{ $t('views.objectives.modal.defaultSection') }}</h2>
                        <div class="relative" :class="['rounded-md transition-colors', { 'objective-row-flash': highlighted.has('default') }]">
                            <CustomInput v-model:value="directoryDefault" :label="$t('views.objectives.modal.targetLabel')" input-type="number" step="0.1" min="0" max="100" :required="false" :rules="[]"></CustomInput>
                            <div class="absolute right-3 top-1/2 flex -translate-y-1/2 flex-col text-gray-400">
                                <button type="button" class="flex h-4 w-5 items-center justify-center hover:text-primary-700" @click="stepDefault(0.1)"><ChevronUp class="h-3.5 w-3.5" /></button>
                                <button type="button" class="flex h-4 w-5 items-center justify-center hover:text-primary-700" @click="stepDefault(-0.1)"><ChevronDown class="h-3.5 w-3.5" /></button>
                            </div>
                        </div>
                        <p class="text-xs text-gray-400">{{ $t('views.objectives.modal.leaveEmptyHint') }}</p>
                    </div>

                    <!-- Per-farm overrides -->
                    <div class="space-y-1">
                        <h2 class="text-base font-semibold text-gray-900">{{ $t('views.objectives.modal.overridesSection') }}</h2>

                        <p v-if="!loading && rows.length === 0" class="pt-2 text-sm text-gray-500">{{ $t('views.objectives.modal.noFarms') }}</p>

                        <template v-else>
                            <div class="grid items-center gap-4 border-b border-gray-200 pb-2 pt-1 text-xs font-medium uppercase tracking-wide text-gray-400" :style="gridCols">
                                <span>{{ $t('views.objectives.modal.colFarm') }}</span>
                                <span>{{ $t('views.objectives.modal.colEffective') }}</span>
                                <span>{{ $t('views.objectives.modal.colOverride') }}</span>
                            </div>

                            <div class="max-h-72 overflow-y-auto divide-y divide-gray-100">
                                <div
                                    v-for="row in rows"
                                    :key="row.locationId"
                                    :ref="(el) => setRowEl(row.locationId, el as Element | null)"
                                    class="grid items-center gap-4 py-3"
                                    :class="{ 'objective-row-flash rounded-md': highlighted.has(row.locationId) }"
                                    :style="gridCols"
                                >
                                    <span class="min-w-0 truncate text-sm font-medium text-gray-800" :title="row.name">{{ row.name }}</span>

                                    <div class="space-y-1">
                                        <p class="text-sm font-semibold text-gray-900">{{ effectiveLabel(row) }}</p>
                                        <span v-if="row.override !== null" class="inline-block rounded bg-emerald-50 px-1.5 py-0.5 text-[0.65rem] font-medium text-emerald-700">{{ $t('views.objectives.modal.badgeCustom') }}</span>
                                        <span v-else-if="directoryDefault !== null" class="inline-block rounded bg-gray-100 px-1.5 py-0.5 text-[0.65rem] font-medium text-gray-500">{{ $t('views.objectives.modal.badgeDefault') }}</span>
                                        <span v-else class="inline-block rounded bg-gray-100 px-1.5 py-0.5 text-[0.65rem] font-medium text-gray-400">{{ $t('views.objectives.modal.badgeNone') }}</span>
                                    </div>

                                    <div class="flex items-center gap-3">
                                        <template v-if="isEditing(row)">
                                            <div class="relative w-28">
                                                <CustomInput v-model:value="row.override" label="" input-type="number" step="0.1" min="0" max="100" :required="false" :rules="[]"></CustomInput>
                                                <div class="absolute right-2 top-1/2 flex -translate-y-1/2 flex-col text-gray-400">
                                                    <button type="button" class="flex h-4 w-5 items-center justify-center hover:text-primary-700" @click="stepRow(row, 0.1)"><ChevronUp class="h-3.5 w-3.5" /></button>
                                                    <button type="button" class="flex h-4 w-5 items-center justify-center hover:text-primary-700" @click="stepRow(row, -0.1)"><ChevronDown class="h-3.5 w-3.5" /></button>
                                                </div>
                                            </div>
                                            <button type="button" class="shrink-0 text-sm font-medium text-primary-700 hover:underline" @click="useDefault(row)">
                                                {{ $t('views.objectives.modal.resetButton') }}
                                            </button>
                                        </template>
                                        <button
                                            v-else
                                            type="button"
                                            class="rounded-lg border border-primary-600 px-3 py-1.5 text-sm font-medium text-primary-700 transition-colors hover:bg-primary-50"
                                            @click="setCustom(row)"
                                        >
                                            {{ $t('views.objectives.modal.setCustom') }}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </div>
                </div>

                <!-- Footer: Close leaves the modal open for one-at-a-time edits; Save only enables when something changed. -->
                <div class="flex justify-end gap-3 border-t border-gray-100 px-8 py-4">
                    <Button variant="outline" @click="() => onClose(scope.close)">{{ $t('views.objectives.modal.close') }}</Button>
                    <Button variant="default" :loading="saving" :disabled="!isDirty" @click="onSave">{{ $t('views.objectives.modal.saveChanges') }}</Button>
                </div>
            </div>
        </template>
    </Modal>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, nextTick } from 'vue';
import { Modal } from '@makeabledk/vue-ui/components/modal';
import { Target, ChevronUp, ChevronDown } from 'lucide-vue-next';
import { store } from '@/plugins/store';
import { ActionType } from '@/plugins/store/actions';
import { i18n } from '@/plugins/internationalization/i18n';
import { MortalitySettingsResponse } from '@/types/MortalityObjective';
import CustomInput from '@/components/ui/CustomInput.vue';
import CloseModalButton from '@/components/common/CloseModalButton.vue';
import PigTypePicker from '@/components/ui/PigTypePicker.vue';
import { Button } from '@/components/ui/button';

interface OverrideRow {
    locationId: number;
    name: string;
    override: number | null;
}

const GRID_COLS = 'grid-template-columns: 1fr 7rem minmax(12rem, 1fr)';

export default defineComponent({
    components: { Modal, CustomInput, CloseModalButton, PigTypePicker, Button, Target, ChevronUp, ChevronDown },
    props: {
        isWeaner: {
            type: Boolean,
            default: false,
        },
        // Hides the modal's own open button for consumers that only open it programmatically via
        // the `openForFarm` exposed method (e.g. Active groups' "Edit target" hand-off) - the
        // Objectives page keeps it, since that's the only entry point for its own "Targets" button.
        showTrigger: {
            type: Boolean,
            default: true,
        },
    },
    emits: ['saved'],
    setup(props, { emit }) {
        const directoryDefault = ref<number | null>(null);
        const rows = ref<OverrideRow[]>([]);
        const loading = ref(false);
        const saving = ref(false);
        const highlighted = ref<Set<number | string>>(new Set());
        const isOpen = ref(false);
        // Rows the user has opened for editing but not yet given a value (override still null).
        const editingRows = ref<Set<number>>(new Set());

        // Snapshot of last-saved values; reactive so the dirty check re-evaluates after a save.
        const savedDefault = ref<number | null>(null);
        const savedOverrides = ref<Map<number, number | null>>(new Map());

        const isDirty = computed<boolean>(() => {
            if (directoryDefault.value !== savedDefault.value) return true;
            return rows.value.some((r) => r.override !== (savedOverrides.value.get(r.locationId) ?? null));
        });

        async function load() {
            loading.value = true;
            try {
                const settings: MortalitySettingsResponse = await store.dispatch(ActionType.GetMortalityObjectiveSettings, {
                    options: { cacheResponse: false, ignoreCache: true },
                });
                directoryDefault.value = settings.directoryDefaultTarget ?? null;
                rows.value = settings.farms.map((f) => ({ locationId: f.locationId, name: f.name, override: f.overrideValue ?? null }));
                editingRows.value = new Set();
                savedDefault.value = directoryDefault.value;
                savedOverrides.value = new Map(rows.value.map((r) => [r.locationId, r.override]));
            } finally {
                loading.value = false;
            }
        }

        function openAndLoad(open: CallableFunction) {
            highlighted.value = new Set();
            isOpen.value = true;
            load();
            open();
        }

        // Captured once from the Modal's own `state` event (open/close), so openForFarm below can
        // trigger the same open the internal toggle button does, without needing that button's click
        // event/scope.open - the "Set" button on an Objectives table row lives outside this component.
        let modalApi: { open: () => void } | null = null;
        function captureModalApi(state: { open: () => void }) {
            modalApi = state;
        }

        // Row elements keyed by locationId, so openForFarm can scroll to and focus a specific farm's
        // override input once its data has loaded.
        const rowEls = new Map<number, Element>();
        function setRowEl(locationId: number, el: Element | null) {
            if (el) rowEls.set(locationId, el);
            else rowEls.delete(locationId);
        }

        // Opens the modal pre-focused on one farm's override input - the "Set" button on an
        // Objectives table row with no resolved target calls this instead of making the user find and
        // open that farm themselves. Awaits `load()` (unlike openAndLoad, which fires it and forgets)
        // because the farm has to actually be in `rows` before it can be found/edited/scrolled to.
        async function openForFarm(locationId: number) {
            highlighted.value = new Set();
            isOpen.value = true;
            modalApi?.open();
            await load();

            const row = rows.value.find((r) => r.locationId === locationId);
            if (!row) return;

            setCustom(row);
            await nextTick();

            const el = rowEls.get(locationId);
            el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            el?.querySelector('input')?.focus();
        }

        function onClose(close: CallableFunction) {
            isOpen.value = false;
            close();
        }

        // Switching the in-modal pig-type picker re-targets which animal type's objectives we edit;
        // reload that type's settings (and drop unsaved edits) while the modal stays open.
        watch(
            () => props.isWeaner,
            () => {
                if (isOpen.value) {
                    highlighted.value = new Set();
                    load();
                }
            }
        );

        // A row shows the override input once it has a value or the user has chosen to set one.
        function isEditing(row: OverrideRow): boolean {
            return row.override !== null || editingRows.value.has(row.locationId);
        }

        function setCustom(row: OverrideRow) {
            editingRows.value = new Set(editingRows.value).add(row.locationId);
        }

        function useDefault(row: OverrideRow) {
            row.override = null;
            const next = new Set(editingRows.value);
            next.delete(row.locationId);
            editingRows.value = next;
        }

        // Round to one decimal so 0.1 steps don't accumulate float drift; keep within 0–100.
        const step = (value: number, delta: number): number => Math.min(100, Math.max(0, Math.round((value + delta) * 10) / 10));

        function stepDefault(delta: number) {
            directoryDefault.value = step(directoryDefault.value ?? 0, delta);
        }

        function stepRow(row: OverrideRow, delta: number) {
            row.override = step(row.override ?? directoryDefault.value ?? 0, delta);
        }

        function effectiveLabel(row: OverrideRow): string {
            const effective = row.override ?? directoryDefault.value;
            return effective == null ? '—' : `${i18n.global.n(effective)}%`;
        }

        async function save() {
            saving.value = true;
            try {
                const affected: (number | string)[] = [];
                const writes: Promise<void>[] = [];

                if (directoryDefault.value !== savedDefault.value) {
                    affected.push('default');
                    writes.push(
                        store.dispatch(ActionType.SetMortalityObjective, {
                            form: { isWeaner: props.isWeaner, locationId: null, targetValue: directoryDefault.value },
                        })
                    );
                }

                for (const row of rows.value) {
                    if (row.override !== savedOverrides.value.get(row.locationId)) {
                        affected.push(row.locationId);
                        writes.push(
                            store.dispatch(ActionType.SetMortalityObjective, {
                                form: { isWeaner: props.isWeaner, locationId: row.locationId, targetValue: row.override },
                            })
                        );
                    }
                }

                if (writes.length === 0) {
                    return;
                }

                await Promise.all(writes);

                // Re-snapshot so the dirty check resets and a second Save only sends further changes.
                savedDefault.value = directoryDefault.value;
                savedOverrides.value = new Map(rows.value.map((r) => [r.locationId, r.override]));

                highlighted.value = new Set(affected);
                window.setTimeout(() => {
                    highlighted.value = new Set();
                }, 1600);

                emit('saved');
            } finally {
                saving.value = false;
            }
        }

        async function onSave() {
            try {
                await save();
            } catch {
                // Leave the modal open on failure so the user can retry.
            }
        }

        return {
            directoryDefault,
            rows,
            loading,
            saving,
            highlighted,
            isDirty,
            gridCols: GRID_COLS,
            openAndLoad,
            openForFarm,
            captureModalApi,
            setRowEl,
            onClose,
            isEditing,
            setCustom,
            useDefault,
            stepDefault,
            stepRow,
            effectiveLabel,
            onSave,
        };
    },
});
</script>

<style scoped>
.objective-row-flash {
    animation: objective-flash 1.6s ease-out;
}

@keyframes objective-flash {
    from {
        background-color: rgba(84, 212, 134, 0.4);
    }
    to {
        background-color: transparent;
    }
}
</style>
